export enum TransactionType {
  EXPENSE = 'expense',
  INCOME = 'income'
}

export enum PaymentType {
  FIXED = 'fixed',
  VARIABLE = 'variable'
}

export enum PaymentMethod {
  CASH = 'cash',
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  PIX = 'pix',
  BANK_TRANSFER = 'bank_transfer',
  OTHER = 'other'
}

export interface Transaction {
  id: string;
  type: TransactionType;
  paymentType: PaymentType;
  description: string;
  value: number;
  hasInstallments: boolean;
  installments?: number;
  currentInstallment?: number;
  /**
   * A parcela que cai no mês sendo exibido. **Só exibição** — derivada da
   * visão, nunca gravada. Ver `utils/monthView.ts`: misturar isso com
   * `currentInstallment` fazia a edição regravar a parcela errada.
   */
  displayInstallment?: number;
  /**
   * SÓ de exibição, como `displayInstallment`: o mês ('AAAA-MM') da ocorrência
   * mostrada, quando o filtro por período pela data de pagamento desdobra uma
   * assinatura ou parcelada em várias linhas. Existe para a chave da linha ser
   * única — e nunca é enviado de volta.
   */
  displayOcorrencia?: string;
  /**
   * SÓ de exibição, como `displayInstallment` (15/09): quanto uma MENSAL pesa
   * no mês mostrado — o valor informado daquele mês, a média dos últimos
   * informados, ou o cadastrado. Derivado por `transactionsInMonth`.
   *
   * `value` continua sendo o CADASTRADO: é ele que o formulário de edição
   * lê e grava. Pôr o valor do mês em `value` faria editar a mensal olhando
   * setembro regravar o valor de setembro como o de todos os meses — a
   * armadilha 5b, a mesma da parcela.
   */
  displayValue?: number;
  displayValueOrigem?: 'informado' | 'media' | 'cadastrado';
  /** Valores reais informados por mês, `{ 'AAAA-MM': valor }`. Vem do servidor; nunca volta. */
  monthValues?: Record<string, number>;
  /** 'AAAA-MM' a partir do qual vale o valor cadastrado atual. Vem do servidor. */
  valueSince?: string | null;
  isMonthly: boolean;
  /** Item 9b: null em transferência de reserva — ela não tem método de pagamento. */
  paymentMethod: string | null;
  debitDirection?: 'debit' | 'withdraw' | null;
  debitSourceType?: 'investment' | 'goal' | null;
  debitSourceId?: string | null;
  /** Resolvido pelo backend por join, só para exibição. */
  debitSourceName?: string | null;
  /**
   * Outras moedas (13/10): código da moeda estrangeira (`EUR`) e a quantia
   * nela. `value` continua em reais. Com `debitDirection` é a compra ou a
   * venda da moeda; sem ele, um gasto pago com a moeda, que não sai do caixa.
   */
  moeda?: string | null;
  valorNaMoeda?: number | null;
  /** Quando o dinheiro SAI. Manda em saldo, projeção e no mês em que aparece. */
  date: string;
  /**
   * Quando a compra aconteceu (13/09). `null` = a compra é o pagamento.
   *
   * Vem CRU do backend. Para mostrar ou filtrar, use `dataDaCompraDe`; para
   * mandar de volta, mande o que ela escolheu — nunca `date` preenchida por
   * conveniência, que viraria uma compra inventada no banco (armadilha 5b).
   */
  purchaseDate?: string | null;
  /** Item 16a: último mês em que uma recorrente conta. Só se aplica a isMonthly. */
  endDate?: string | null;
  categoryId?: string;
  /**
   * Da casa ou pessoal. `undefined` quando o esquema da casa não existe neste
   * banco — a tela trata como "sem marca", que é `pessoal`.
   */
  escopo?: 'casa' | 'pessoal';
  createdAt: string;
}
